import { useState } from 'react'

import { errorText } from '../../lib/offsite'
import { isDelegating, isUser } from '../../lib/nav'
import { useSession } from '../../lib/SessionContext'

/* ============================================================================
   ฟอร์มยื่นใบลา / ปรับปรุงเวลา / OT
   API: POST /api/me/leave · POST /api/me/ot (ดู backend/src/routes/me.js)

   ── ใครเห็นฟอร์มไหน ────────────────────────────────────────────────────────
   SubmitLeave : พนักงานยื่นของตัวเอง หรือ admin ที่เข้าแทน (delegate) ยื่นแทนได้
   SubmitOt    : เฉพาะ admin ที่เข้าแทน (OT ยื่นเองผ่านหน้านี้ยังไม่เปิด)

   ตรงกับ backend: POST /me/leave เปิดให้ทั้งสอง · POST /me/ot ยัง requireDelegate
   ============================================================================ */

const LEAVE_TYPES: { code: string; th: string }[] = [
  { code: '001', th: 'ลากิจ' },
  { code: '002', th: 'ลาพักร้อน' },
  { code: '003', th: 'ลาป่วย' },
  { code: '004', th: 'ลากิจ ไม่รับค่าจ้าง' },
  { code: '998', th: 'ลาคลอดบุตร' },
  { code: '994', th: 'ลาอุปสมบท' },
  { code: '993', th: 'ลาเพื่อสมรส' },
  // 999 = ปรับปรุงเวลา (ไม่ใช่การลา ไม่ตัดโควตา) — ปลดล็อกให้ยื่นได้แล้ว
  { code: '999', th: 'ปรับปรุงเวลา (ไม่ใช่การลา)' },
]

type QuotaInfo = {
  entitlementDays?: number
  usedDays?: number
  requestDays?: number
  remainDays?: number
}

/** ยื่นใบลา / ปรับปรุงเวลา — พนักงานเอง หรือ admin ยื่นแทน */
export function SubmitLeave({ empName, onDone }: { empName: string | null; onDone: () => void }) {
  const { state, call } = useSession()
  const [open, setOpen] = useState(false)
  const [leaveType, setLeaveType] = useState('001')
  const [dateStart, setDateStart] = useState('')
  const [dateEnd, setDateEnd] = useState('')
  const [reason, setReason] = useState('')
  const [evidence, setEvidence] = useState<string | null>(null)
  const [evidenceName, setEvidenceName] = useState<string>('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ok, setOk] = useState<string | null>(null)
  // ถ้ายื่นแล้วเกินสิทธิ์ backend ตอบ 409 + ข้อมูลโควตา → admin ยืนยันยื่นเกินได้
  const [overQuota, setOverQuota] = useState<QuotaInfo | null>(null)

  if (state.status !== 'signed-in') return null
  const delegating = isDelegating(state.session)
  // แสดงฟอร์มเมื่อเป็นพนักงาน (ยื่นของตัวเอง) หรือ admin ที่เข้าแทน
  if (!isUser(state.session)) return null

  const isAdjust = leaveType === '999'

  // อ่านไฟล์เป็น data URL (รูป/PDF ≤6MB) — ตรงกับที่ backend รับ (saveEvidence)
  function onPickFile(file: File | null) {
    setError(null)
    if (!file) {
      setEvidence(null)
      setEvidenceName('')
      return
    }
    const okTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf']
    if (!okTypes.includes(file.type)) {
      setError('แนบได้เฉพาะ JPG, PNG, WEBP, PDF')
      return
    }
    if (file.size > 6 * 1024 * 1024) {
      setError(`ไฟล์ใหญ่ ${Math.round(file.size / 1024 / 1024)} MB — จำกัด 6 MB`)
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      setEvidence(typeof reader.result === 'string' ? reader.result : null)
      setEvidenceName(file.name)
    }
    reader.onerror = () => setError('อ่านไฟล์ไม่สำเร็จ')
    reader.readAsDataURL(file)
  }

  async function submit(skipQuotaCheck = false) {
    setBusy(true)
    setError(null)
    setOk(null)
    try {
      const r = await call<{ result: { rowId: string; evidence?: { error?: string } | null } }>(
        '/api/me/leave',
        {
          method: 'POST',
          body: JSON.stringify({ leaveType, dateStart, dateEnd, reason, skipQuotaCheck, evidence }),
        },
      )
      const label = isAdjust ? 'ใบปรับปรุงเวลา' : 'ใบลา'
      const evErr = r.result.evidence?.error
      setOk(
        `ยื่น${label}แล้ว เลขที่ ${r.result.rowId} (สถานะ รออนุมัติ)` +
          (evErr ? ` — แต่แนบไฟล์ไม่สำเร็จ: ${evErr}` : evidence ? ' · แนบไฟล์แล้ว' : ''),
      )
      setDateStart('')
      setDateEnd('')
      setReason('')
      setEvidence(null)
      setEvidenceName('')
      setOverQuota(null)
      onDone()
    } catch (err) {
      // 409 เกินสิทธิ์ — backend แนบ quota มาด้วย (เฉพาะกรณีนี้)
      const q = (err as { quota?: QuotaInfo })?.quota
      if (q && delegating) {
        setOverQuota(q)
        setError(errorText(err))
      } else {
        setOverQuota(null)
        setError(errorText(err))
      }
    } finally {
      setBusy(false)
    }
  }

  const title = delegating ? `ยื่นใบลา / ปรับปรุงเวลาแทน ${empName ?? ''}` : 'ยื่นใบลา / ปรับปรุงเวลา'

  return (
    <section className="card form-card">
      <div className="section-head">
        <h2>{title}</h2>
        <button type="button" className="ghost" onClick={() => setOpen((v) => !v)}>
          {open ? 'ซ่อน' : 'ยื่นใบ'}
        </button>
      </div>
      {open && (
        <>
          <p className="meta">
            {delegating
              ? 'ยื่นแทนพนักงาน — ใบจะขึ้นสถานะ “รออนุมัติ” และบันทึกว่าคุณเป็นผู้ยื่น'
              : 'ใบจะขึ้นสถานะ “รออนุมัติ” แล้ววิ่งตามสายหัวหน้าของคุณ'}
          </p>
          <div className="field-grid">
            <label className="kv">
              <span className="kv-label">ประเภท</span>
              <select
                value={leaveType}
                onChange={(e) => {
                  setLeaveType(e.target.value)
                  setOverQuota(null)
                }}
              >
                {LEAVE_TYPES.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.th}
                  </option>
                ))}
              </select>
            </label>
            <label className="kv">
              <span className="kv-label">ตั้งแต่</span>
              <input
                type="datetime-local"
                value={dateStart}
                onChange={(e) => setDateStart(e.target.value)}
              />
            </label>
            <label className="kv">
              <span className="kv-label">ถึง</span>
              <input
                type="datetime-local"
                value={dateEnd}
                onChange={(e) => setDateEnd(e.target.value)}
              />
            </label>
            <label className="kv field-grid--full">
              <span className="kv-label">เหตุผล</span>
              <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} />
            </label>
            <label className="kv field-grid--full">
              <span className="kv-label">แนบหลักฐาน</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
              />
              {evidenceName && <small className="hint">แนบ: {evidenceName}</small>}
            </label>
          </div>
          {isAdjust && (
            <p className="meta">ปรับปรุงเวลา = ขอแก้เวลาเข้า–ออก ไม่ใช่การลา และไม่ตัดโควต้าวันลา</p>
          )}
          {error && <p className="error">{error}</p>}
          {ok && <p className="ok">{ok}</p>}
          <div className="row-actions">
            <button
              type="button"
              onClick={() => void submit(false)}
              disabled={busy || !dateStart || !dateEnd || !reason.trim()}
            >
              {busy ? 'กำลังยื่น…' : 'ยื่นใบ'}
            </button>
            {/* เกินสิทธิ์ + เป็น admin ที่เข้าแทน → ยืนยันยื่นเกินได้ (พนักงานเองทำไม่ได้) */}
            {overQuota && delegating && (
              <button
                type="button"
                className="ghost"
                onClick={() => void submit(true)}
                disabled={busy}
              >
                ยืนยันยื่นเกินสิทธิ์ (เหลือ {overQuota.remainDays} ขอ {overQuota.requestDays} วัน)
              </button>
            )}
          </div>
        </>
      )}
    </section>
  )
}

/** ยื่น OT แทน */
export function SubmitOt({ empName, onDone }: { empName: string | null; onDone: () => void }) {
  const { state, call } = useSession()
  const [open, setOpen] = useState(false)
  const [timeStart, setTimeStart] = useState('')
  const [timeEnd, setTimeEnd] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ok, setOk] = useState<string | null>(null)

  if (state.status !== 'signed-in' || !isDelegating(state.session)) return null

  async function submit() {
    setBusy(true)
    setError(null)
    setOk(null)
    try {
      const r = await call<{ result: { rowId: string } }>('/api/me/ot', {
        method: 'POST',
        body: JSON.stringify({ timeStart, timeEnd, reason }),
      })
      setOk(`ยื่น OT แทนแล้ว เลขที่ ${r.result.rowId} (สถานะ รออนุมัติ · เรตให้ HR กำหนดตอนอนุมัติ)`)
      setTimeStart('')
      setTimeEnd('')
      setReason('')
      onDone()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="card form-card">
      <div className="section-head">
        <h2>ยื่น OT แทน {empName ?? ''}</h2>
        <button type="button" className="ghost" onClick={() => setOpen((v) => !v)}>
          {open ? 'ซ่อน' : 'ยื่น OT'}
        </button>
      </div>
      {open && (
        <>
          <p className="meta">
            ยื่นแทนพนักงาน — ใบขึ้นสถานะ “รออนุมัติ” · เรต OT ให้ HR กำหนดตอนอนุมัติ
          </p>
          <div className="field-grid">
            <label className="kv">
              <span className="kv-label">เริ่ม</span>
              <input
                type="datetime-local"
                value={timeStart}
                onChange={(e) => setTimeStart(e.target.value)}
              />
            </label>
            <label className="kv">
              <span className="kv-label">ถึง</span>
              <input
                type="datetime-local"
                value={timeEnd}
                onChange={(e) => setTimeEnd(e.target.value)}
              />
            </label>
            <label className="kv field-grid--full">
              <span className="kv-label">เหตุผล</span>
              <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} />
            </label>
          </div>
          {error && <p className="error">{error}</p>}
          {ok && <p className="ok">{ok}</p>}
          <div className="row-actions">
            <button
              type="button"
              onClick={submit}
              disabled={busy || !timeStart || !timeEnd || !reason.trim()}
            >
              {busy ? 'กำลังยื่น…' : 'ยื่น OT แทน'}
            </button>
          </div>
        </>
      )}
    </section>
  )
}
